import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import BlockManager, { BlockManagerConfig, BlockContainer, UnifiedBlock } from '../services/blockManager';
import { ContentBlock } from '../services/blockService';

export interface UseBlockManagerConfig extends Omit<BlockManagerConfig, 'callbacks'> {
  // Container-specific update function
  onContainerUpdate?: (container: BlockContainer) => Promise<void>;
  // Initial block IDs to load
  initialBlockIds?: string[];
}

export interface UseBlockManagerReturn {
  blockManager: BlockManager;
  contentBlocks: UnifiedBlock[];
  syncStatus: 'synced' | 'syncing' | 'error';
  loading: boolean;
  
  // Utility functions
  loadBlocks: (blockIds: string[]) => Promise<void>;
  getBlockIds: () => string[];
  setContainerId: (containerId: string) => void;
}

/**
 * Custom hook for standardized block management across components
 */
export const useBlockManager = (config: UseBlockManagerConfig): UseBlockManagerReturn => {
  const [contentBlocks, setContentBlocks] = useState<UnifiedBlock[]>([]);
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error'>('synced');
  const [loading, setLoading] = useState(false);
  const blockManagerRef = useRef<BlockManager | null>(null);
  
  // Stable refs for config values to prevent unnecessary recreations
  const userIdRef = useRef(config.userId);
  const containerTypeRef = useRef(config.containerType);
  const containerIdRef = useRef(config.containerId);
  const onContainerUpdateRef = useRef(config.onContainerUpdate);
  
  // Update refs when config changes
  userIdRef.current = config.userId;
  containerTypeRef.current = config.containerType;
  containerIdRef.current = config.containerId;
  onContainerUpdateRef.current = config.onContainerUpdate;

  // Stable callback refs
  const onBlocksChangedRef = useRef((blocks: UnifiedBlock[]) => {
    // Add safety check for undefined/null blocks
    const validBlocks = blocks.filter(block => block && block.block_id);
    console.log('🔄 useBlockManager: blocks changed, count:', validBlocks.length, 'ids:', validBlocks.map(b => b.block_id));
    setContentBlocks(validBlocks);
  });
  
  const onSyncStatusChangedRef = useRef((status: 'synced' | 'syncing' | 'error') => {
    setSyncStatus(status);
  });

  // Initialize block manager - create once, update container as needed
  const initializeBlockManager = useCallback(() => {
    const currentContainerId = containerIdRef.current;
    const currentUserId = userIdRef.current;
    const currentContainerType = containerTypeRef.current;

    // Create manager if it doesn't exist
    if (!blockManagerRef.current) {
      const blockManager = new BlockManager({
        userId: currentUserId,
        containerType: currentContainerType,
        containerId: currentContainerId,
        callbacks: {
          onBlocksChanged: onBlocksChangedRef.current,
          onContainerUpdated: onContainerUpdateRef.current,
          onSyncStatusChanged: onSyncStatusChangedRef.current
        }
      });

      blockManagerRef.current = blockManager;
      return blockManager;
    }

    // Update container ID if it changed (much more efficient than recreation)
    const existingManager = blockManagerRef.current;
    if (existingManager.getContainerId() !== currentContainerId) {
      existingManager.setContainerId(currentContainerId || '');
    }

    return existingManager;
  }, []); // Empty dependency array - uses refs for current values

  // Get block manager instance
  const blockManager = useMemo(() => initializeBlockManager(), [initializeBlockManager]);

  // Load blocks function - use ref to avoid recreating on every render
  const loadBlocks = useCallback(async (blockIds: string[]) => {
    let manager = blockManagerRef.current;
    
    // If manager is not available, try to initialize it
    if (!manager) {
      manager = initializeBlockManager();
    }
    
    if (!manager) {
      console.warn('❌ No blockManager available for loadBlocks after initialization attempt');
      return;
    }
    
    setLoading(true);
    try {
      await manager.loadContentBlocks(blockIds);
    } catch (error) {
      console.error('❌ Failed to load blocks:', error);
      setSyncStatus('error');
    } finally {
      setLoading(false);
    }
  }, [initializeBlockManager]); // Include initializeBlockManager in dependencies

  // Load initial blocks if provided
  useEffect(() => {
    if (config.initialBlockIds && config.initialBlockIds.length > 0) {
      loadBlocks(config.initialBlockIds);
    }
  }, [config.initialBlockIds, loadBlocks]);

  // Utility functions - use refs for stability

  const getBlockIds = useCallback(() => {
    let manager = blockManagerRef.current;
    
    // If manager is not available, try to initialize it
    if (!manager) {
      manager = initializeBlockManager();
    }
    
    return manager?.getBlockIds() || [];
  }, [initializeBlockManager]); // Include initializeBlockManager in dependencies

  const setContainerId = useCallback((containerId: string) => {
    let manager = blockManagerRef.current;
    
    // If manager is not available, try to initialize it
    if (!manager) {
      manager = initializeBlockManager();
    }
    
    manager?.setContainerId(containerId);
    containerIdRef.current = containerId; // Update ref as well
  }, [initializeBlockManager]); // Include initializeBlockManager in dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      blockManagerRef.current?.destroy();
      // Don't set blockManagerRef.current = null here because React Strict Mode
      // calls cleanup functions twice in development, which would break the hook
    };
  }, []);

  return {
    blockManager,
    contentBlocks,
    syncStatus,
    loading,
    loadBlocks,
    getBlockIds,
    setContainerId
  };
};

export default useBlockManager;
